import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";

const FORM_ADDR = "https://readdy.ai/api/form/db96gn1rh5tg6sock43g";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，感谢你的关注！");
      setEmail("");
      return;
    }
    formData.delete("website_alt");
    setStatus("loading");
    setMessage("");
    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，感谢你的关注！");
        setEmail("");
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
      }
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "网络异常，请稍后再试。");
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form" className="w-full">
      <label htmlFor="newsletter-email" className="sr-only">
        邮箱地址
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          id="newsletter-email"
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="输入邮箱，获取最新影视资讯"
          className="flex-1 px-4 py-2.5 text-sm rounded-md bg-primary-800 border border-primary-700 text-background-50 placeholder:text-primary-300 focus:outline-none focus:ring-2 focus:ring-accent-400"
        />
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="verify-field-extra"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="px-5 py-2.5 text-sm font-medium rounded-md bg-accent-500 text-primary-950 hover:bg-accent-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
        >
          {status === "loading" ? "提交中..." : "立即订阅"}
        </button>
      </div>
      {message && (
        <p
          className={`mt-2 text-xs ${status === "success" ? "text-accent-300" : "text-accent-200"}`}
          role="status"
        >
          {message}
        </p>
      )}
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="w-full bg-primary-900 text-background-100 mt-16">
      <div className="w-full px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-md bg-accent-500 text-primary-950 flex items-center justify-center text-lg">
                <i className="ri-vip-crown-line" aria-hidden="true"></i>
              </span>
              <span className="font-heading text-xl font-bold text-background-50">赏金女王</span>
            </div>
            <p className="text-sm leading-relaxed text-primary-200">
              赏金女王影视大全，专注最新电视剧与影视资讯，为你提供权威及时的影视作品库、剧情介绍与行业动态。
            </p>
          </div>

          <div>
            <h4 id="footer-nav" className="text-sm font-semibold text-background-50 mb-4">
              <a href="#footer-nav" className="hover:text-accent-300 transition-colors">
                快速导航
              </a>
            </h4>
            <ul className="space-y-2.5 text-sm text-primary-200">
              <li>
                <Link to="/" className="hover:text-accent-300 transition-colors">
                  首页
                </Link>
              </li>
              <li>
                <Link to="/category/tv" className="hover:text-accent-300 transition-colors">
                  电视剧
                </Link>
              </li>
              <li>
                <Link to="/category/movie" className="hover:text-accent-300 transition-colors">
                  电影
                </Link>
              </li>
              <li>
                <Link to="/news" className="hover:text-accent-300 transition-colors">
                  影视资讯
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 id="footer-services" className="text-sm font-semibold text-background-50 mb-4">
              <a href="#footer-services" className="hover:text-accent-300 transition-colors">
                热门分类
              </a>
            </h4>
            <ul className="space-y-2.5 text-sm text-primary-200">
              <li>
                <Link to="/category/classic" className="hover:text-accent-300 transition-colors">
                  经典影视
                </Link>
              </li>
              <li>
                <Link to="/category/tv" className="hover:text-accent-300 transition-colors">
                  古装仙侠
                </Link>
              </li>
              <li>
                <Link to="/category/tv" className="hover:text-accent-300 transition-colors">
                  悬疑犯罪
                </Link>
              </li>
              <li>
                <Link to="/search" className="hover:text-accent-300 transition-colors">
                  全站搜索
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 id="footer-subscribe" className="text-sm font-semibold text-background-50 mb-4">
              <a href="#footer-subscribe" className="hover:text-accent-300 transition-colors">
                订阅影视资讯
              </a>
            </h4>
            <p className="text-sm text-primary-200 mb-3">订阅后第一时间获取最新剧集与行业动态。</p>
            <NewsletterForm />
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-primary-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-primary-300">
          <p>© 2025 赏金女王影视大全 · 本站内容仅供影视资讯与剧情介绍参考</p>
          <p>站内不提供真实播放与下载，收藏与分享均为演示功能</p>
        </div>
      </div>
    </footer>
  );
}